// Public API: resolveDottedBase(buffer, cursor) → DottedBase | null. Pure function.
// Turns `a.b.c.` text at the cursor into a structured { database?, schema?, relation? }
// binding. Handles 1- through 4-part names; Oracle `@dblink` suffix surfaces via the
// resolved QualifiedName.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Dotted-base Resolver.
//
// Algorithm:
//   1. Walk LEFT from the cursor to find the start of the dotted chain
//      (see dottedParts.ts — traverses idents, dots, `@dblink` in Oracle,
//      and quoted-identifier bodies).
//   2. Parse the chain forward into DottedPart[] + optional dblink.
//   3. Resolve against the current Scope:
//        1 part  → alias | reachable table | schema
//        2 parts → alias.column OR schema.table — aliases win
//        3 parts → db.schema.table (MSSQL/PG)
//        4 parts → server.db.schema.obj (MSSQL)
//   4. Oracle `@dblink` attaches to the relation's QualifiedName.dblink.
//   5. Return `null` when no meaningful resolution is possible.

import type { ParseOptions } from '../../sql-grammar/model';
import type {
  AliasBinding,
  DottedBase,
  ParsedBuffer,
  ReachableTable,
  Scope,
  SqlPosition,
} from '../types';
import { resolveScope } from '../scope/resolveScope';
import {
  findDottedChainStart,
  parseDottedChain,
  makeQualifiedName,
  withDblink,
  type DottedPart,
} from './dottedParts';

type Resolved = DottedBase['resolved'];

export function resolveDottedBase(buffer: ParsedBuffer, cursor: SqlPosition): DottedBase | null {
  const { source, options } = buffer.parsedDocument;
  const offset = Math.min(Math.max(cursor.offset, 0), source.length);

  const start = findDottedChainStart(source, offset, options);
  if (start >= offset) return null;

  const chain = parseDottedChain(source, start, offset, options);
  if (!chain || chain.parts.length === 0) return null;

  const scope = resolveScope(buffer, { ...cursor, offset });
  const resolved = resolveParts(chain.parts, chain.dblink, scope, options);
  if (!resolved) return null;

  return {
    parts: chain.parts.map((p) => p.value),
    ranges: chain.parts.map((p) => p.range),
    resolved,
  };
}

function resolveParts(
  parts: readonly DottedPart[],
  dblink: string | undefined,
  scope: Scope,
  options: ParseOptions,
): Resolved | null {
  switch (parts.length) {
    case 1: return resolveOnePart(parts[0], dblink, scope, options);
    case 2: return resolveTwoParts(parts[0], parts[1], dblink, scope, options);
    case 3: return resolveThreeParts(parts, dblink, scope, options);
    case 4: return resolveFourParts(parts, dblink, scope, options);
    default: return null;
  }
}

function resolveOnePart(
  part: DottedPart,
  dblink: string | undefined,
  scope: Scope,
  options: ParseOptions,
): Resolved | null {
  const alias = findAliasInScope(scope, part.folded);
  if (alias) return { relation: alias };
  const rt = findReachableTable(scope, part.folded, undefined);
  if (rt) return dblink ? { relation: withDblink(rt, dblink, options.engine) } : { relation: rt };
  if (schemaInScope(scope, part.folded)) {
    return { schema: makeQualifiedName([part]) };
  }
  return null;
}

function resolveTwoParts(
  a: DottedPart,
  b: DottedPart,
  dblink: string | undefined,
  scope: Scope,
  options: ParseOptions,
): Resolved | null {
  // alias.column — aliases win over schema.table ambiguity (legacy heuristic).
  const alias = findAliasInScope(scope, a.folded);
  if (alias) return { relation: alias };
  const rt = findReachableTable(scope, b.folded, a.folded);
  const schemaQN = makeQualifiedName([a]);
  if (rt) {
    const relation = dblink ? withDblink(rt, dblink, options.engine) : rt;
    return { schema: schemaQN, relation };
  }
  return { schema: schemaQN };
}

function resolveThreeParts(
  parts: readonly DottedPart[],
  dblink: string | undefined,
  scope: Scope,
  options: ParseOptions,
): Resolved {
  const databaseQN = makeQualifiedName([parts[0]]);
  const schemaQN = makeQualifiedName([parts[0], parts[1]]);
  const rt = findReachableTable(scope, parts[2].folded, parts[1].folded);
  const relation = rt ? (dblink ? withDblink(rt, dblink, options.engine) : rt) : undefined;
  return { database: databaseQN, schema: schemaQN, relation };
}

function resolveFourParts(
  parts: readonly DottedPart[],
  dblink: string | undefined,
  scope: Scope,
  options: ParseOptions,
): Resolved {
  // MSSQL: server.db.schema.object — v1 surfaces db + schema + best-effort relation.
  const databaseQN = makeQualifiedName([parts[0], parts[1]]);
  const schemaQN = makeQualifiedName([parts[1], parts[2]]);
  const rt = findReachableTable(scope, parts[3].folded, parts[2].folded);
  const relation = rt ? (dblink ? withDblink(rt, dblink, options.engine) : rt) : undefined;
  return { database: databaseQN, schema: schemaQN, relation };
}

function findAliasInScope(scope: Scope, folded: string): AliasBinding | undefined {
  let s: Scope | undefined = scope;
  while (s) {
    const hit = s.aliases.find((a) => a.aliasFolded === folded);
    if (hit) return hit;
    s = s.parent;
  }
  return undefined;
}

function findReachableTable(
  scope: Scope,
  nameFolded: string,
  schemaFolded: string | undefined,
): ReachableTable | undefined {
  let s: Scope | undefined = scope;
  while (s) {
    const hit = s.reachableTables.find((r) => {
      if (r.qualified.foldedName !== nameFolded) return false;
      if (schemaFolded === undefined) return true;
      return r.qualified.foldedSchema === schemaFolded;
    });
    if (hit) return hit;
    s = s.parent;
  }
  return undefined;
}

function schemaInScope(scope: Scope, folded: string): boolean {
  if (scope.currentSchema && scope.currentSchema.toLowerCase() === folded) return true;
  let s: Scope | undefined = scope;
  while (s) {
    for (const r of s.reachableTables) {
      if (r.qualified.foldedSchema === folded) return true;
    }
    s = s.parent;
  }
  return false;
}
